import { Link } from "wouter";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";

/**
 * Which Instance the user is working in, named in the top bar (UX-1, FR-20).
 *
 * The label carries the tenant as well as the instance, because instance names repeat across
 * tenants ("Production" exists in every one of them) and the only thing worse than picking the
 * wrong instance is not noticing. It is the tenant's Display name, never its Name (owner
 * 2026-10-01: "Northwind Marketing · Production"). The instance's environment label is not shown: the owner asked
 * "what is DEV?" of the badge that did (round 3, 2026-09-30).
 *
 * It is plain text, not a control (round 4, owner 2026-09-30: "showing Tenant name and instance
 * name is alone good in the header. no need for it to be dropdown"). The user menu holds the one
 * picker, with "All instances" under it, so the bar no longer repeats it. The tenant is muted, the
 * instance medium weight; the label truncates, so the whole of it is in `title` (hover) and in the
 * accessible name. The `<fieldset>` (role group) is what lets the element carry that name.
 *
 * With NO instance selected, a platform administrator on the deployment-wide screens (which
 * read no instance header), the slot says so and links to the picker page. Rendering nothing there
 * instead leaves the bar looking like a shell that lost its instance rather than one that never
 * needed it (FR-20). #921: with no instance to open at all (a new deployment), it says so in
 * plain text: a link to a picker with nothing on it leads nowhere.
 */
export function InstanceSwitcher() {
  const { selectedInstance, instances } = useTenantInstance();

  if (!selectedInstance && instances.length === 0) {
    return (
      <span
        data-testid="instance-none"
        className="flex items-center px-2 py-1 text-sm text-topbar-foreground/70"
      >
        No instance yet
      </span>
    );
  }

  if (!selectedInstance) {
    return (
      <Link
        href="/instances"
        data-testid="instance-choose"
        className="flex items-center rounded-md px-2 py-1 text-sm text-topbar-foreground/70 transition-opacity hover:underline hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Choose an instance
      </Link>
    );
  }

  const label = `${selectedInstance.tenantDisplayName} · ${selectedInstance.name}`;
  return (
    <fieldset
      data-testid="instance-picker"
      title={label}
      aria-label={label}
      className="flex min-w-0 max-w-[22rem] items-center px-2 py-1 text-sm text-topbar-foreground"
    >
      {/* Below `sm` only the instance name shows (the label still names both): the tenant
          would take the few characters a phone has. `max-sm:`, not `hidden sm:inline`:
          skypoint-ui re-ships `.hidden` after the app's utilities and would win. */}
      <span className="truncate">
        <span className="text-topbar-foreground/70 max-sm:hidden">
          {selectedInstance.tenantDisplayName}
        </span>
        <span className="px-1 text-topbar-foreground/40 max-sm:hidden">·</span>
        <span className="font-medium">{selectedInstance.name}</span>
      </span>
    </fieldset>
  );
}
